/**
 * Sending the list to a shop (#1294).
 *
 * ⛔ IT NEVER ORDERS ANYTHING, and the screen says so unprompted. It assembles;
 * a person opens the cart, reads it and pays. An app that spends money on a
 * mis-parsed quantity loses that household permanently.
 *
 * ⚠️ Kroger and Instacart both need approval, so today this reports honestly
 * that nothing is connected rather than presenting a button that quietly does
 * nothing.
 */
import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { twoPlates } from "../api/twoPlates";

export function SendToShop({ planId }: { planId: number }) {
  const [open, setOpen] = useState(false);
  const { data } = useQuery({
    queryKey: ["cart-retailers"],
    queryFn: twoPlates.cartRetailers,
    enabled: open,
  });

  const prepare = useMutation({
    mutationFn: (retailer: string) => twoPlates.prepareCart(planId, retailer),
  });

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="text-muted text-sm underline underline-offset-4 print:hidden"
      >
        Send it to a shop
      </button>
    );
  }

  const result = prepare.data;

  return (
    <div className="border-line rounded-xl border p-4 print:hidden">
      <p className="text-muted text-xs uppercase tracking-wide">Send it to a shop</p>

      {!result && (
        <>
          <div className="mt-3 flex flex-col gap-2">
            {(data?.retailers ?? []).map((r) => (
              <button
                key={r.key}
                onClick={() => prepare.mutate(r.key)}
                disabled={prepare.isPending}
                className="border-line rounded-lg border p-3 text-left disabled:opacity-50"
              >
                <p className="text-sm font-medium">
                  {r.name}
                  {!r.configured && <span className="text-muted"> · not connected</span>}
                </p>
                <p className="text-muted mt-0.5 text-xs">{r.note}</p>
              </button>
            ))}
          </div>
          <p className="text-muted mt-3 text-xs">
            Nothing here ever places an order. It builds a cart; you open it, read
            it and pay.
          </p>
        </>
      )}

      {result && (
        <div className="mt-3">
          {result.message && <p className="text-muted text-sm">{result.message}</p>}

          <p className="mt-3 text-sm">
            {result.coverage.matched} of {result.coverage.total} already known at this shop.
          </p>

          {result.toConfirm.length > 0 && (
            <p className="text-muted mt-2 text-xs">
              {result.toConfirm.length} need picking once. After that they are remembered.
            </p>
          )}

          {result.notes.length > 0 && (
            <div className="mt-3">
              <p className="text-muted text-xs uppercase tracking-wide">Pick these up yourself</p>
              <ul className="mt-1.5 flex flex-col gap-1 text-xs">
                {result.notes.map((n) => <li key={n} className="text-muted">{n}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      <button onClick={() => setOpen(false)} className="text-muted mt-4 text-xs underline underline-offset-4">
        Close
      </button>
    </div>
  );
}
